import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Loader2, Upload, CreditCard, Users } from 'lucide-react';
import { mediaUrl } from '@/lib/mediaUrl';

const FIELDS = ['name', 'contact_email', 'billing_email', 'phone', 'website', 'address', 'logo_url'];

const Text = ({ label, k, form, set, type = 'text', hint }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`org-settings-${k}`} />
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

export default function OrganisationSettingsPage() {
  const { api } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get('/organisations/my').then((r) => { setOrg(r.data); setForm(Object.fromEntries(FIELDS.map((k) => [k, r.data[k] || '']))); })
      .catch(() => setOrg({ error: true }));
  }, [api]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploadLogo = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try { const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); set('logo_url', r.data.url); }
    catch { toast.error('Logo upload failed'); } finally { setUploading(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Organisation name is required'); return; }
    setBusy(true);
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
    if (!payload.contact_email) delete payload.contact_email;
    try { const r = await api.put(`/organisations/${org.id}`, payload); setOrg(r.data); toast.success('Organisation details saved'); }
    catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Please check the details and try again'); }
    finally { setBusy(false); }
  };
  return (
    <DashboardLayout requiredRoles={['org_admin', 'super_admin']}>
      <div className="space-y-6 max-w-3xl" data-testid="org-settings-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Organisation settings</h1>
          <p className="text-slate-500 mt-1 text-sm">These details appear on reports and invitations sent to your team.</p>
        </div>
        {!org ? <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
          : org.error ? <p className="text-slate-600" data-testid="org-settings-missing">Your account isn't linked to an organisation yet.</p> : (
            <>
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-[#2a3e5d]"><Building2 className="h-5 w-5 text-[#20acf6]" />Organisation details</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={save} className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl border bg-slate-50 overflow-hidden flex items-center justify-center">
                        {form.logo_url ? <img src={mediaUrl(form.logo_url)} alt={`${form.name} logo`} className="w-full h-full object-contain" /> : <Building2 className="h-6 w-6 text-slate-300" />}
                      </div>
                      <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload logo
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} data-testid="org-settings-logo-upload" />
                      </label>
                    </div>
                    <Text label="Organisation name" k="name" form={form} set={set} />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Text label="Contact email" k="contact_email" type="email" form={form} set={set} />
                      <Text label="Billing email" k="billing_email" type="email" form={form} set={set} hint="Receipts and invoices go here" />
                      <Text label="Phone" k="phone" form={form} set={set} />
                      <Text label="Website" k="website" form={form} set={set} />
                    </div>
                    <div className="space-y-1.5"><Label>Address</Label><Textarea rows={3} value={form.address} onChange={(e) => set('address', e.target.value)} data-testid="org-settings-address" /></div>
                    <Button type="submit" disabled={busy} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="org-settings-save">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save changes</Button>
                  </form>
                </CardContent>
              </Card>
              <div className="grid sm:grid-cols-2 gap-4">
                <Button asChild variant="outline" className="h-14 justify-start"><Link to="/organisation/subscription" data-testid="org-settings-subscription"><CreditCard className="h-4 w-4 mr-2" />Plan & billing</Link></Button>
                <Button asChild variant="outline" className="h-14 justify-start"><Link to="/organisation/team" data-testid="org-settings-team"><Users className="h-4 w-4 mr-2" />Manage team ({org.team_member_count ?? 0})</Link></Button>
              </div>
            </>
          )}
      </div>
    </DashboardLayout>
  );
}
